import React, { useState } from "react";
import PropTypes from "prop-types";

import Card from "react-bootstrap/Card";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

import { FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";

import be from "../BE";

function CalendarDiagnostics({ calendarsList, showTestRsvp, onRsvpConfirmed }) {
  // RSVP-test state
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpLink, setRsvpLink] = useState("");
  const [rsvpError, setRsvpError] = useState("");

  // RSVP-confirm state
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 1) Filter broken connections
  const problematicCalendars = Array.isArray(calendarsList)
    ? calendarsList.filter((cal) => cal.stopLight === false)
    : [];

  // Popover for calendar‐connection help
  const connectionPopover = (
    <Popover id="connection-help-popover" placement="left">
      <Popover.Header as="h3">Fix Calendar Connection</Popover.Header>
      <Popover.Body>
        <img
          src="/app-assets/img/google-calendar-checkbox.png"
          alt="Google Calendar 'Make changes to events' checkbox"
          style={{ width: "100%", borderRadius: 4, marginBottom: 10 }}
        />
        <p>
          Please Disconnect and Reconnect your google account, being sure to
          check the calendar permissions box.
        </p>
      </Popover.Body>
    </Popover>
  );

  // Popover for RSVP‐test guide
  const rsvpPopover = (
    <Popover id="rsvp-guide-popover">
      <Popover.Header as="h3">How RSVP Testing Works</Popover.Header>
      <Popover.Body>
        <p>If you did not receive the event, please</p>
        <p>
          read the full guide{" "}
          <a
            href="https://calendbook.medium.com/ensuring-rsvps-appear-across-multiple-google-calendars-db67fe2d05c9"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-underline"
          >
            here
          </a>
          .
        </p>
      </Popover.Body>
    </Popover>
  );

  // ─── Connection‐issue alerts ───
  if (problematicCalendars.length > 0) {
    return (
      <Card
        style={{
          color: "var(--text-color)",
          background: "var(--color-background-1)",
        }}
        className="shadow-sm"
      >
        <Card.Header
          style={{
            borderBottom: "1px solid rgba(0,0,0,0.125)",
            fontWeight: 600,
            fontSize: "1.125rem",
          }}
        >
          Calendar Diagnostics
        </Card.Header>
        <Card.Body>
          {problematicCalendars.map((cal) => (
            <Alert
              key={cal.email}
              variant="warning"
              className="d-flex align-items-start mb-3"
            >
              <FaExclamationTriangle
                style={{
                  fontSize: "1.5rem",
                  marginRight: "0.5rem",
                  marginTop: "0.15rem",
                }}
              />
              <div style={{ flex: 1 }}>
                <strong>{cal.email}</strong>
                <br />
                Connection issue. Modify permissions.
                <OverlayTrigger
                  trigger="click"
                  placement="left"
                  overlay={connectionPopover}
                  rootClose
                >
                  <Button variant="link" className="p-0 align-baseline ms-1">
                    <FaInfoCircle size={14} />
                  </Button>
                </OverlayTrigger>
              </div>
            </Alert>
          ))}
        </Card.Body>
      </Card>
    );
  }

  // ─── If no connection issues & no RSVP test requested → nothing ───
  if (!showTestRsvp) {
    return null;
  }

  // ─── RSVP Test Handler ───
  const handleTestRsvp = async () => {
    setRsvpLoading(true);
    setRsvpError("");
    setRsvpLink("");

    try {
      const response = await be.post(
        "calendarhandler",
        "/calendars/rsvp",
        false,
        true
      );
      setRsvpLink(response.rsvpLink);
    } catch (error) {
      let msg = "RSVP test failed unexpectedly";
      let errorStatus = error?.response?.data?.status;
      if (errorStatus === 422) {
        msg = "RSVP test Failed";
      }
      if (errorStatus === 400) {
        msg = "RSVP needs at leas one event type available";
      }
      setRsvpError(msg);
    } finally {
      setRsvpLoading(false);
    }
  };

  // ─── RSVP Confirm Handler ───
  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      await be.post(
        "calendarhandler",
        "/calendars/rsvp/confirmed",
        false,
        true
      );
    } catch (err) {
      console.warn("RSVP confirmation failed:", err);
    } finally {
      await onRsvpConfirmed();
      setConfirmLoading(false);
    }
  };

  return (
    <Card
      style={{
        color: "var(--text-color)",
        background: "var(--color-background-1)",
      }}
      className="shadow-sm"
    >
      <Card.Header
        style={{
          borderBottom: "1px solid rgba(0,0,0,0.125)",
          fontWeight: 600,
          fontSize: "1.125rem",
        }}
      >
        Calendar Diagnostics
      </Card.Header>
      <Card.Body>
        <div className="mb-3" style={{ fontSize: "1rem" }}>
          Check your calendars: have you received the RSVP in all your
          calendars?
        </div>

        <div className="d-flex align-items-center mb-3">
          <Button
            variant="primary"
            onClick={handleTestRsvp}
            disabled={rsvpLoading || confirmLoading}
          >
            {rsvpLoading && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            Test RSVP
          </Button>
        </div>

        {rsvpLink && (
          <>
            <Alert variant="success">
              Calendbook created a test event!{" "}
              <a
                href={rsvpLink}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-underline"
              >
                Check it here
              </a>
              .
            </Alert>

            <div className="mt-3 mb-2" style={{ fontSize: "1rem" }}>
              Did the test event appear correctly in your calendar?
            </div>
            <Button
              variant="success"
              onClick={handleConfirm}
              disabled={confirmLoading}
            >
              {confirmLoading && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              Yes
            </Button>
            {/* Info icon for RSVP test */}
            <OverlayTrigger
              trigger="click"
              placement="right"
              overlay={rsvpPopover}
              rootClose
            >
              <Button
                variant="secondary"
                className="tw-inline-flex tw-align-middle tw-items-center tw-gap-2 tw-ms-2"
              >
                <span>No</span>
                <FaInfoCircle size={16} />
              </Button>
            </OverlayTrigger>
          </>
        )}

        {rsvpError && (
          <Alert variant="danger" className="mt-3">
            {rsvpError}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}

CalendarDiagnostics.propTypes = {
  calendarsList: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      stopLight: PropTypes.bool.isRequired,
    })
  ).isRequired,
  showTestRsvp: PropTypes.bool.isRequired,
  onRsvpConfirmed: PropTypes.func.isRequired,
};

export default CalendarDiagnostics;
